import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ErrorMessage, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import {
  SettingRuleInitialValues,
  SettingRuleValidationSchema,
  PortalSettingRule,
} from "../../../models/features/ShopifySetting/Portal Setting Rules/PortalSettingRules";
import { useAddShoppingPortalRuleMutation } from "../../../redux/reducer/features/Setting/Shopping Portal/ShoppingPortalSettingSlice";
import {
  useGetDmsManufacturersQuery,
  useGetDmsPartsMutation,
} from "../../../redux/reducer/features/ShoppingPortal/ShoppingPortalSlice";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { DmsManufacturer } from "../../../models/features/ShoppingPortal/DmsManufacturer";
import {
  DmsPart,
  DmsPartRequestDto,
} from "../../../models/features/ShoppingPortal/DmsPart";

interface AddRuleProps {
  handleClose: (fetch: boolean) => void;
  settingRule?: PortalSettingRule;
}
const AddPortalRule: React.FunctionComponent<AddRuleProps> = ({
  handleClose,
  settingRule,
}) => {
  const dmsLocations = useSelector(
    (state: AppState) => state.authReducer?.dmsSetup?.dmsLocations,
  );
  const [getDmsPartsMutation, getDmsPartsMutationResult] =
    useGetDmsPartsMutation();
  const [
    getDmsPartsByInventoryPartIdsMutation,
    getDmsPartsByInventoryPartIdsMutationResult,
  ] = useGetDmsPartsMutation();
  const [addRuleMutation, addRuleMutationResult] =
    useAddShoppingPortalRuleMutation();
  const { data: dmsManufacturersList } = useGetDmsManufacturersQuery({
    dealerId: dmsLocations ? (dmsLocations[0].dealerId.toString() ?? "") : "",
  });

  const [settingRuleValues, setsettingRuleValues] = useState<
    PortalSettingRule | undefined
  >(settingRule);
  const [search, setSearch] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [defaultMfgs, setDefaultMfgs] = useState<DmsManufacturer[]>([]);
  const [defaultParts, setDefaultParts] = useState<DmsPart[]>([]);
  const [dmsManufacturers, setDmsManufacturers] = useState<DmsManufacturer[]>(
    [],
  );
  const [dmsParts, setDmsParts] = useState<DmsPart[]>([]);

  React.useEffect(() => {
    if (settingRule && settingRule?.manufacturers) {
      let mfgs = dmsManufacturers.filter((mfg) =>
        settingRule?.manufacturers?.some((item: string) => item === mfg.code),
      );
      setDefaultMfgs(mfgs);
    }
  }, [dmsManufacturers]);

  React.useEffect(() => {
    if (settingRule && settingRule?.partNumbers.length > 0) {
      fetchDmsPartsByInventoryPartIds();
    }
  }, [settingRule]);

  React.useEffect(() => {
    if (dmsLocations) {
      fetchDmsPartsData();
    }
  }, [dmsLocations]);
  React.useEffect(() => {
    if (
      dmsManufacturersList?.entityList &&
      dmsManufacturersList.entityList?.length > 0
    ) {
      setDmsManufacturers(dmsManufacturersList.entityList);
    }
  }, [dmsManufacturersList]);

  const handleOnSubmit = (values: PortalSettingRule) => {
    setErrorMessage("");
    if (settingRule === undefined) {
      values.id = undefined;
    }
    addRuleMutation({ data: values }).then((res: any) => {
      if (res && res.data) {
        if (res.data.status === 200) {
          handleClose(true);
        } else if (res.data.status === 404) {
          setErrorMessage(
            "you have not registered your Shopping Portal yet, that's why you can't add any rule.",
          );
        } else {
          setErrorMessage(
            "Sorry! We caught an error while adding rule. Please try again later.",
          );
        }
      }
    });
  };

  const fetchDmsPartsData = () => {
    var data: DmsPartRequestDto = {
      dealerIds:
        dmsLocations
          ?.filter((n) => n.storeLocationId > 0)
          .map((n: any) => n.dealerId.toString()) ?? [],
      pageSize: 20,
      pageNumber: 0,
      stockingOnly: true,
      searchPartNumber: [],
      searchPartDescription: "",
      search: search,
      searchManufacturerId: "",
      searchManufacturerCode: [],
      searchManufacturerCodeNotIn: [],
      inventoryPartIdNotIn: [],
    };
    getDmsPartsMutation({ data: data })
      .then((response: any) => {
        if (response?.data && response?.data?.entityList.length > 0) {
          setDmsParts(
            response.data.entityList.map((part: DmsPart) => ({
              ...part,
              id: part.inventoryPartId,
            })),
          );
        }
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const fetchDmsPartsByInventoryPartIds = () => {
    var data: DmsPartRequestDto = {
      dealerIds: dmsLocations?.map((n: any) => n.dealerId.toString()) ?? [],
      pageSize: 100,
      pageNumber: 0,
      stockingOnly: true,
      searchPartNumber: [],
      searchPartDescription: "",
      search: search,
      searchManufacturerId: "",
      searchManufacturerCode: [],
      searchManufacturerCodeNotIn: [],
      inventoryPartIdNotIn: [],
      searchPartId: settingRule?.partNumbers ?? [],
    };
    getDmsPartsByInventoryPartIdsMutation({ data: data })
      .then((response: any) => {
        if (response?.data && response?.data?.entityList.length > 0) {
          setDefaultParts(
            response.data.entityList.map((part: DmsPart) => ({
              ...part,
              id: part.inventoryPartId,
            })),
          );
        }
      })
      .catch((error) => {})
      .finally(() => {});
  };

  useEffect(() => {
    if (search) {
      const timerId = setTimeout(() => {
        fetchDmsPartsData();
      }, 1500);
      return () => clearTimeout(timerId);
    }
  }, [search]);

  return (
    <>
      <Formik
        initialValues={settingRuleValues ?? SettingRuleInitialValues}
        enableReinitialize={true}
        validationSchema={SettingRuleValidationSchema}
        onSubmit={(values) => {
          handleOnSubmit(values);
        }}
      >
        {({ values, errors, handleChange, handleBlur, setFieldValue }) => (
          <>
            <Form>
              <Grid container spacing={3} alignItems="center" mt={1}>
                <Grid item xs={4}>
                  <Typography>Rule Title:</Typography>
                </Grid>

                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    placeholder="Rule Title"
                    size="small"
                    variant="outlined"
                    name="settingTitle"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.settingTitle}
                  />
                  <ErrorMessage
                    className="error-msg"
                    component={"div"}
                    name="settingTitle"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography>Hide Parts:</Typography>
                </Grid>

                <Grid item xs={8}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.hideParts}
                        color="secondary"
                        name="hideParts"
                        value={values.hideParts}
                        onChange={handleChange}
                      />
                    }
                    label=""
                  />
                </Grid>
                {values.hideParts === false ? (
                  <>
                    <Grid item xs={4}>
                      <Typography>Hide Price:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.hidePrice}
                            color="secondary"
                            name="hidePrice"
                            value={values.hidePrice}
                            onChange={(e) => {
                              setFieldValue("hidePrice", e.target.checked);
                              setFieldValue("hideCartButton", e.target.checked);
                            }}
                          />
                        }
                        label=""
                      />
                    </Grid>
                    {values.hidePrice === false ? null : (
                      <>
                        {" "}
                        <Grid item xs={4}>
                          <Typography>Add Text to Replace Price:</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            placeholder="Text to replace price"
                            size="small"
                            variant="outlined"
                            name="priceReplacementText"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.priceReplacementText}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={4}>
                      <Typography>Hide "Add To Cart" buttons:</Typography>
                    </Grid>

                    <Grid item xs={8}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.hideCartButton}
                            disabled={values.hidePrice}
                            color="secondary"
                            name="hideCartButton"
                            value={values.hideCartButton}
                            onChange={handleChange}
                          />
                        }
                        label=""
                      />
                    </Grid>
                    {values.hideCartButton === false ? null : (
                      <>
                        <Grid item xs={4}>
                          <Typography>
                            Add text for hover on cart icon:
                          </Typography>
                        </Grid>

                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            placeholder="Add text for hover on cart icon"
                            size="small"
                            variant="outlined"
                            name="cartReplacementText"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.cartReplacementText}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={4}>
                      <Typography>Apply On All Parts:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.applyOnAllParts}
                            color="secondary"
                            name="applyOnAllParts"
                            value={values.applyOnAllParts}
                            onChange={handleChange}
                          />
                        }
                        label=""
                      />
                    </Grid>
                  </>
                ) : (
                  ""
                )}

                {values.applyOnAllParts || values.hideParts ? (
                  ""
                ) : (
                  <React.Fragment>
                    <Grid item xs={4}>
                      <Typography>Manufacturers:</Typography>
                    </Grid>

                    <Grid item xs={8}>
                      <Autocomplete
                        multiple
                        options={dmsManufacturers}
                        getOptionLabel={(option) => option.name}
                        value={defaultMfgs}
                        id="clear-on-escape"
                        clearOnEscape
                        onChange={(e, value) => {
                          setDefaultMfgs(value);
                          setFieldValue(
                            "manufacturers",
                            value.map((item) => item.code),
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </React.Fragment>
                )}
                {values.applyOnAllParts ? (
                  ""
                ) : (
                  <React.Fragment>
                    <Grid item xs={4}>
                      <Typography>Part Numbers:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Autocomplete
                        multiple
                        options={dmsParts}
                        getOptionLabel={(option) =>
                          option.mfrName + " - " + option.partNumber
                        }
                        value={defaultParts}
                        loading={getDmsPartsMutationResult.isLoading}
                        onChange={(e, value) => {
                          setDefaultParts(value);
                          setFieldValue(
                            "partNumbers",
                            value.map((item) => item.inventoryPartId),
                          );
                        }}
                        onInputChange={(event, newInputValue) => {
                          setSearch(newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            sx={{ fontSize: "10px" }}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </React.Fragment>
                )}
                <Grid item xs={4}>
                  <Typography>Status:</Typography>
                </Grid>

                <Grid item xs={8}>
                  <Select
                    id="status"
                    size="small"
                    name="status"
                    fullWidth
                    defaultValue={values.status}
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value={"true"}>Enable </MenuItem>
                    <MenuItem value={"false"}>Disable </MenuItem>
                  </Select>
                  <ErrorMessage
                    className="error-msg"
                    component={"div"}
                    name="status"
                  />
                </Grid>
                <Grid item xs={12}>
                  {errorMessage.length > 0 ? (
                    <Alert severity="error"> {errorMessage} </Alert>
                  ) : (
                    ""
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  gap={2}
                  justifyContent="flex-end"
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      handleClose(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={() => {
                      console.log(errors);
                      console.log(values);
                    }}
                    endIcon={
                      addRuleMutationResult.isLoading ? (
                        <CircularProgress color="inherit" size={25} />
                      ) : (
                        <></>
                      )
                    }
                    disabled={addRuleMutationResult.isLoading}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};

export default AddPortalRule;
